const en = {
  sftTitle: 'Today’s service frequency',
  sftDescription:
    'Bangkok service-day cohort by provider, including providers with no trips. Accepted and completed are recorded milestones, not mutually exclusive outcomes. Customer finance is not agent payout. Agents counted here have accepted assignment evidence; they are not necessarily online.',
  sftDateBasis:
    'Scheduled start, or trip creation when scheduled start is unavailable; Bangkok request-day is frozen for exports.',
  sftFilter_serviceMode: 'Service mode',
  sftFilter_isFree: 'Welfare / free booking',
  sftFilter_businessDate: 'Expected Bangkok business date',
  sftMode_RIDE_AND_CARE: 'Ride and care',
  sftMode_CARE: 'Care',
  sftMode_RIDE: 'Ride',
  sftMode_TRAVEL: 'Travel',
  sftValueServiceDate: 'Service date',
  sftValueCurrentScope: 'Current service-group mapping',
  sftValueActiveBasis: 'Accepted assignment in service-day cohort',
  sftSummaryTripCount: 'Trips in today’s service cohort',
  sftColumn_provider_id: 'Provider ID',
  sftColumn_provider_name: 'Provider name',
  sftColumn_business_date: 'Bangkok business date',
  sftColumn_date_basis: 'Date basis',
  sftColumn_scope_provenance: 'Scope provenance',
  sftColumn_scheduled_date_trip_count: 'Trips dated by scheduled start',
  sftColumn_created_date_fallback_trip_count: 'Trips dated by creation fallback',
  sftColumn_requests_count: 'Trips in service cohort',
  sftColumn_accepted_trip_count: 'Trips with acceptance evidence',
  sftColumn_completed_trip_count: 'Trips with completion evidence',
  sftColumn_cancelled_trip_count: 'Currently cancelled trips',
  sftColumn_missed_trip_count: 'Currently missed trips',
  sftColumn_active_agent_count: 'Distinct agents accepting assignments in today’s cohort',
  sftColumn_active_agent_basis: 'Agent-count basis',
  sftColumn_currency: 'Currency',
  sftColumn_fare_total_sum: 'Persisted customer fare total (THB)',
  sftColumn_billed_total_sum: 'Customer billed total (THB)',
  sftColumn_received_total_sum: 'Customer received total (THB)',
  sftColumn_outstanding_total_sum: 'Customer outstanding total (THB)',
  sftColumn_free_trip_count: 'Trips in FREE payment state',
  sftColumn_finance_review_count: 'Trips requiring finance review',
  sftColumn_missing_fare_count: 'Trips missing persisted fare',
  sftColumn_missing_accepted_evidence_count: 'Assigned or progressed trips missing acceptance evidence',
  sftColumn_missing_completed_evidence_count: 'Completed trips missing completion evidence',
  sftColumn_missing_agent_evidence_count: 'Accepted trips missing agent identity evidence',
  sftSourceProvider:
    'Current provider master. Each selected provider has a row even when the filtered cohort is empty.',
  sftSourceFrozenDay:
    'Bangkok day resolved by the server at request time. Saved from/to bounds stay fixed even if the worker runs after midnight.',
  sftSourceServiceDate:
    'Service date is scheduled_start, falling back to created_at. The population is the half-open Bangkok day [from, to).',
  sftSourceCurrentScope:
    'Area filters use the current service-group area mapping, not historical dispatch evaluation areas. A trip is counted only once.',
  sftSourceScheduled: 'Distinct cohort trips with a persisted scheduled start.',
  sftSourceFallback: 'Distinct cohort trips without scheduled start, included by their creation timestamp.',
  sftSourceRequests:
    'Distinct trips whose service date is in the selected day, not bookings created or notifications sent today.',
  sftSourceAccepted:
    'Distinct cohort trips with an ACCEPTED persisted offer or ASSIGNED / REASSIGNED status event. An accepted trip may also be cancelled.',
  sftSourceCompleted:
    'Distinct cohort trips with a persisted COMPLETED event. Current COMPLETED status alone does not invent milestone evidence.',
  sftSourceCancelled: 'Distinct cohort trips currently in CANCELLED state at the report snapshot.',
  sftSourceMissed: 'Distinct cohort trips currently in MISSED state at the report snapshot.',
  sftSourceActiveAgents:
    'Distinct recorded agent IDs from accepted offers or assignment events in this service cohort, never current online presence or the current trip assignee. Grand total deduplicates agents across providers.',
  sftSourceFinance:
    'Customer finance uses the canonical persisted-trip/payment projection, in THB; it is not agent earnings or payout.',
  sftSourceFare:
    'Sum of non-null persisted current fare totals. Missing fares are omitted, reported separately, and never repriced or rounded by the report.',
  sftSourceBilled:
    'Canonical sum of customer payment amounts due. Cancelled and missed trips contribute zero billed amount.',
  sftSourceReceived:
    'Canonical customer amounts received, including terminal-trip receipts; this is not net cash after refunds.',
  sftSourceOutstanding:
    'Canonical unresolved customer balance. Cancelled and missed trips contribute zero outstanding amount.',
  sftSourceFree: 'Count of canonical payment_state = FREE, not an alternative isFree-only finance rule.',
  sftSourceReview:
    'Canonical finance-review cases, including missing fare with payment and unrefunded terminal-trip receipts.',
  sftSourceMissingFare:
    'Trips without persisted current fare. A zero fare sum with this count does not imply complete fare coverage.',
  sftSourceMissingAccepted:
    'Assigned or progressed current-status trips with no accepted-offer or assignment-event evidence. No acceptance is inferred from current status.',
  sftSourceMissingCompleted:
    'Current COMPLETED trips without a persisted COMPLETED event; completion count remains evidence-based.',
  sftSourceMissingAgent:
    'Accepted-evidence trips with no valid recorded agent UUID in accepted offers or assignment events. No current-assignee fallback is used.',
  sftEmptyZero:
    'Zero means no matching facts in the filtered cohort. Empty providers are retained with numeric zeros.',
  sftEmptyRequired: 'Required provider/day or declared provenance value; not a guessed historical value.',
} as const;

const th: Record<keyof typeof en, string> = {
  sftTitle: 'ความถี่บริการวันนี้',
  sftDescription:
    'สรุป cohort ตามวันบริการกรุงเทพฯ แยกผู้ให้บริการ รวมผู้ให้บริการที่ไม่มีเที่ยว การรับงานและสำเร็จเป็นหลักฐาน milestone และอาจทับซ้อนกับผลลัพธ์อื่น เงินเป็นยอดเรียกเก็บลูกค้าไม่ใช่ค่าตอบแทนพนักงาน จำนวนพนักงานมาจากหลักฐานรับงาน ไม่ใช่สถานะออนไลน์',
  sftDateBasis: 'วันนัดเริ่มบริการ หรือวันสร้างเที่ยวเมื่อไม่มีวันนัด โดยตรึงวันกรุงเทพฯ ณ เวลาร้องขอสำหรับการส่งออก',
  sftFilter_serviceMode: 'รูปแบบบริการ',
  sftFilter_isFree: 'การจองสวัสดิการ / ฟรี',
  sftFilter_businessDate: 'วันธุรกิจกรุงเทพฯ ที่คาดไว้',
  sftMode_RIDE_AND_CARE: 'เดินทางพร้อมดูแล',
  sftMode_CARE: 'ดูแล',
  sftMode_RIDE: 'เดินทาง',
  sftMode_TRAVEL: 'ท่องเที่ยว',
  sftValueServiceDate: 'วันบริการ',
  sftValueCurrentScope: 'การจับคู่พื้นที่ของกลุ่มบริการปัจจุบัน',
  sftValueActiveBasis: 'การรับงานใน cohort วันบริการ',
  sftSummaryTripCount: 'เที่ยวใน cohort บริการวันนี้',
  sftColumn_provider_id: 'รหัสผู้ให้บริการ',
  sftColumn_provider_name: 'ชื่อผู้ให้บริการ',
  sftColumn_business_date: 'วันธุรกิจกรุงเทพฯ',
  sftColumn_date_basis: 'เกณฑ์วันที่',
  sftColumn_scope_provenance: 'ที่มาของขอบเขต',
  sftColumn_scheduled_date_trip_count: 'เที่ยวที่ใช้วันนัดเริ่ม',
  sftColumn_created_date_fallback_trip_count: 'เที่ยวที่ใช้วันสร้างแทน',
  sftColumn_requests_count: 'เที่ยวใน cohort วันบริการ',
  sftColumn_accepted_trip_count: 'เที่ยวที่มีหลักฐานรับงาน',
  sftColumn_completed_trip_count: 'เที่ยวที่มีหลักฐานสำเร็จ',
  sftColumn_cancelled_trip_count: 'เที่ยวที่ยกเลิก ณ ปัจจุบัน',
  sftColumn_missed_trip_count: 'เที่ยวที่พลาด ณ ปัจจุบัน',
  sftColumn_active_agent_count: 'พนักงานไม่ซ้ำที่รับงานใน cohort วันนี้',
  sftColumn_active_agent_basis: 'เกณฑ์จำนวนพนักงาน',
  sftColumn_currency: 'สกุลเงิน',
  sftColumn_fare_total_sum: 'รวมค่าบริการลูกค้าที่บันทึกไว้ (บาท)',
  sftColumn_billed_total_sum: 'รวมยอดเรียกเก็บลูกค้า (บาท)',
  sftColumn_received_total_sum: 'รวมยอดรับจากลูกค้า (บาท)',
  sftColumn_outstanding_total_sum: 'รวมยอดค้างชำระลูกค้า (บาท)',
  sftColumn_free_trip_count: 'เที่ยวที่มีสถานะการเงิน FREE',
  sftColumn_finance_review_count: 'เที่ยวที่ต้องตรวจสอบการเงิน',
  sftColumn_missing_fare_count: 'เที่ยวที่ไม่มีค่าบริการบันทึกไว้',
  sftColumn_missing_accepted_evidence_count: 'เที่ยวที่มอบหมายหรือเดินหน้าแล้วแต่ขาดหลักฐานรับงาน',
  sftColumn_missing_completed_evidence_count: 'เที่ยวสำเร็จที่ขาดหลักฐานสำเร็จ',
  sftColumn_missing_agent_evidence_count: 'เที่ยวรับงานที่ขาดหลักฐานตัวตนพนักงาน',
  sftSourceProvider: 'ข้อมูลหลักผู้ให้บริการปัจจุบัน มีหนึ่งแถวต่อผู้ให้บริการที่เลือก แม้ไม่มีเที่ยวใน cohort ที่กรองแล้ว',
  sftSourceFrozenDay:
    'เซิร์ฟเวอร์กำหนดวันกรุงเทพฯ ณ เวลาร้องขอ ขอบเขต from/to ที่บันทึกจะไม่เปลี่ยนแม้ worker ทำงานหลังเที่ยงคืน',
  sftSourceServiceDate:
    'วันบริการใช้ scheduled_start หรือ created_at เมื่อไม่มีวันนัด โดยใช้ช่วงวันกรุงเทพฯ [from, to) ไม่รวมเวลาสิ้นสุด',
  sftSourceCurrentScope:
    'ตัวกรองพื้นที่ใช้การจับคู่พื้นที่ของกลุ่มบริการปัจจุบัน ไม่ใช่พื้นที่จากการประเมิน dispatch ในอดีต และนับเที่ยวแต่ละเที่ยวครั้งเดียว',
  sftSourceScheduled: 'จำนวนเที่ยวไม่ซ้ำใน cohort ที่มีวันนัดเริ่มบันทึกไว้',
  sftSourceFallback: 'จำนวนเที่ยวไม่ซ้ำใน cohort ที่ไม่มีวันนัดเริ่ม และรวมตามเวลาสร้างเที่ยว',
  sftSourceRequests: 'เที่ยวไม่ซ้ำที่วันบริการอยู่ในวันที่เลือก ไม่ใช่จำนวนการจองที่สร้างหรือการแจ้งเตือนที่ส่งวันนี้',
  sftSourceAccepted:
    'เที่ยวไม่ซ้ำที่มี offer สถานะ ACCEPTED หรือเหตุการณ์ ASSIGNED / REASSIGNED เที่ยวที่รับงานแล้วอาจถูกยกเลิกด้วย',
  sftSourceCompleted:
    'เที่ยวไม่ซ้ำที่มีเหตุการณ์ COMPLETED บันทึกไว้ สถานะ COMPLETED ปัจจุบันเพียงอย่างเดียวไม่ใช้สร้างหลักฐานย้อนหลัง',
  sftSourceCancelled: 'เที่ยวไม่ซ้ำใน cohort ที่มีสถานะ CANCELLED ณ snapshot รายงาน',
  sftSourceMissed: 'เที่ยวไม่ซ้ำใน cohort ที่มีสถานะ MISSED ณ snapshot รายงาน',
  sftSourceActiveAgents:
    'รหัสพนักงานไม่ซ้ำจาก offer ที่รับงานหรือเหตุการณ์มอบหมายใน cohort นี้ ไม่ใช่สถานะออนไลน์หรือผู้รับผิดชอบปัจจุบันของเที่ยว ยอดรวมตัดพนักงานซ้ำข้ามผู้ให้บริการ',
  sftSourceFinance:
    'การเงินลูกค้าใช้ข้อมูลเที่ยวและการชำระที่บันทึกไว้ตาม projection มาตรฐาน หน่วยบาท ไม่ใช่รายได้หรือค่าตอบแทนพนักงาน',
  sftSourceFare: 'ผลรวมค่าบริการปัจจุบันที่ไม่เป็น null แสดงจำนวนค่าบริการที่ขาดแยกต่างหาก ไม่คำนวณราคาใหม่หรือปัดเศษในรายงาน',
  sftSourceBilled: 'รวมยอดเรียกเก็บลูกค้าตามกฎมาตรฐาน เที่ยวที่ยกเลิกหรือพลาดมีส่วนในยอดเรียกเก็บเป็นศูนย์',
  sftSourceReceived: 'ยอดรับจากลูกค้าตามกฎมาตรฐาน รวมยอดรับของเที่ยวสิ้นสุดแล้ว ไม่ใช่เงินสดสุทธิหลังคืนเงิน',
  sftSourceOutstanding: 'ยอดลูกค้าค้างชำระตามกฎมาตรฐาน เที่ยวที่ยกเลิกหรือพลาดมีส่วนในยอดค้างเป็นศูนย์',
  sftSourceFree: 'จำนวนเที่ยวที่ payment_state = FREE ตามกฎมาตรฐาน ไม่ใช่การเปลี่ยนกฎการเงินโดยนับ isFree เพียงอย่างเดียว',
  sftSourceReview:
    'กรณีต้องตรวจสอบการเงินตามกฎมาตรฐาน รวมเที่ยวไม่มีค่าบริการแต่มีการชำระ และยอดรับที่ยังไม่คืนของเที่ยวสิ้นสุดแล้ว',
  sftSourceMissingFare: 'เที่ยวที่ไม่มีค่าบริการปัจจุบันบันทึกไว้ หากผลรวมเป็นศูนย์แต่จำนวนนี้มากกว่าศูนย์ ไม่ได้หมายถึงข้อมูลค่าบริการครบ',
  sftSourceMissingAccepted:
    'เที่ยวที่มอบหมายหรือเดินหน้าแล้วแต่ไม่มี offer ที่รับงานหรือเหตุการณ์มอบหมาย ไม่อนุมานการรับงานจากสถานะปัจจุบัน',
  sftSourceMissingCompleted: 'เที่ยวสถานะ COMPLETED ปัจจุบันที่ไม่มีเหตุการณ์ COMPLETED จำนวนเที่ยวสำเร็จยังใช้หลักฐานเหตุการณ์',
  sftSourceMissingAgent:
    'เที่ยวที่มีหลักฐานรับงานแต่ไม่มี UUID พนักงานที่ถูกต้องใน offer หรือเหตุการณ์มอบหมาย ไม่ใช้ผู้รับผิดชอบปัจจุบันแทน',
  sftEmptyZero: 'ศูนย์หมายถึงไม่มีข้อเท็จจริงที่ตรงกับ cohort ที่กรอง ผู้ให้บริการที่ไม่มีเที่ยวยังคงมีแถวและตัวเลขศูนย์',
  sftEmptyRequired: 'ค่าผู้ให้บริการ วัน หรือที่มาที่ประกาศไว้ซึ่งต้องมี ไม่ใช่ค่าประวัติที่คาดเดา',
};

export const serviceFrequencyTodayTranslations = { th, en };
